import { Link } from "react-router-dom";
import {
  Facebook, MessageSquareText, MapPin, Truck, Car, Battery, DollarSign,
  HandCoins, Search, ClipboardList, ShieldCheck, Clock, MessageCircle, HeartHandshake,
  Check, ChevronRight,
} from "lucide-react";
import Seo, { breadcrumbs } from "../components/Seo";
// ReviewCta is gated to lead-submit success only.
import { siteConfig } from "../config";

const ABOUT_PHOTO = "https://res.cloudinary.com/diqi6ihgx/image/upload/v1779939751/ChatGPT_Image_May_27_2026_11_42_22_PM_pf4n9b.png";

const TRUST_POINTS = [
  "Over 15 Years Experience",
  "Central Ohio Local",
  "New & Used Vehicle Help",
  "Trade-In Guidance",
  "Payment Help",
  "EV Questions",
];

const HELP_CARDS = [
  { Icon: Car,         title: "New Vehicle Help",  text: "Looking for a new Chevrolet car, truck, SUV, or EV? I can help you compare models, trims, features, lease options, finance options, and available inventory.", cta: "Shop New Vehicles", to: "/new-vehicles", testid: "about-help-new" },
  { Icon: Truck,       title: "Used Vehicle Help", text: "Shopping used? I can help you look through available cars, trucks, SUVs, fresh trades, low-mileage vehicles, one-owner options, and budget-friendly choices.", cta: "Shop Used Vehicles", to: "/used-vehicles", testid: "about-help-used" },
  { Icon: HandCoins,   title: "Trade-In Guidance", text: "Have a trade? I can help you understand what information matters, what affects value, and what steps make the process smoother.", cta: "Value My Trade", to: "/value-my-trade", testid: "about-help-trade" },
  { Icon: DollarSign,  title: "Payment Questions", text: "If you are shopping by payment, I can help explain what affects the numbers, including vehicle price, trade value, money down, payoff, term, taxes, fees, and credit approval.", cta: "Payment Help", to: "/payment-help", testid: "about-help-payment" },
  { Icon: Battery,     title: "EV Questions",      text: "Curious about EVs? I can help explain range, charging, home charging, public charging, fast charging, and what to expect when switching from gas to electric.", cta: "EV Help", to: "/ev-help", testid: "about-help-ev" },
  { Icon: Search,      title: "Vehicle Finder Help", text: "If you do not see exactly what you want, tell me what you are looking for and I'll help watch for the right option.", cta: "Find Me a Vehicle", to: "/find-me-a-vehicle", testid: "about-help-finder" },
];

const VALUES = [
  { Icon: MessageCircle,  title: "Clear Communication",       text: "I'll try to explain things in a way that actually makes sense." },
  { Icon: Clock,          title: "Respect for Your Time",     text: "If you ask about a vehicle, I'll do my best to help you get the details you need." },
  { Icon: ShieldCheck,    title: "No-Pressure Help",          text: "You should not feel pushed into something that does not fit." },
  { Icon: ClipboardList,  title: "Real Vehicle Guidance",     text: "New, used, truck, SUV, EV, trade, payment, or accessories — I'll help you sort through it." },
  { Icon: HeartHandshake, title: "Follow-Up That Feels Human", text: "Not robotic. Not canned. Just useful help." },
];

const CITIES = [
  "Grove City","Columbus","Hilliard","Dublin","Gahanna","Westerville","Reynoldsburg",
  "Pickerington","Groveport","Canal Winchester","Ashville","Circleville","London",
  "Plain City","Delaware","Marysville","Pataskala","Lancaster","Chillicothe","Newark",
];

const SITE_FEATURES = [
  "Search new vehicles","Search used vehicles","Ask Scott a question","Value your trade",
  "Preview a deal in Build My Own Deal","Get payment help","Set vehicle alerts","Schedule a test drive","Contact Scott directly",
];

export default function AboutScott() {
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Scott Hall",
    jobTitle: "Automotive Sales Professional",
    description: "Central Ohio automotive professional with over 15 years of experience helping drivers shop new and used vehicles.",
    url: "https://scottsellschevys.com/about-scott",
    image: ABOUT_PHOTO,
    sameAs: [siteConfig.links.facebook],
  };
  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "Meet Scott Hall",
    url: "https://scottsellschevys.com/about-scott",
    mainEntity: personSchema,
  };

  return (
    <main id="main-content" data-testid="about-scott-page">
      <Seo
        title="Meet Scott Hall | Scott Sells Chevys"
        description="Meet Scott Hall, a Central Ohio automotive professional with over 15 years of experience helping drivers shop new and used vehicles with honest answers, trade-in guidance, payment help, EV knowledge, and a no-pressure approach."
        canonicalPath="/about-scott"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "About Scott", path: "/about-scott" }]),
          aboutPageSchema,
          personSchema,
        ]}
      />

      {/* 1. HERO */}
      <section className="bg-[#F5F8FC]" data-testid="about-hero">
        <div className="ssc-container py-12 md:py-20 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="inline-flex items-center gap-2 text-[10px] font-extrabold tracking-[0.2em] uppercase text-[var(--ssc-blue)] bg-white border border-[#D9E1EA] px-3 py-1.5 rounded-full">
              <MapPin size={12} /> Central Ohio · 15+ years
            </div>
            <h1 className="ssc-display text-4xl md:text-5xl xl:text-6xl font-black leading-[1.02] text-[#081426] mt-4">
              Meet Scott Hall
            </h1>
            <p className="text-lg md:text-xl text-[var(--ssc-ink)] font-semibold mt-3">
              Over 15 years helping Central Ohio drivers find the right new or used vehicle.
            </p>
            <p className="text-base text-[var(--ssc-ink-2)] mt-4 max-w-2xl leading-relaxed">
              I help people shop new and used vehicles with honest answers, clear communication, and a no-pressure approach. Whether you need a new Chevrolet, a used SUV, a truck, an EV, trade-in help, or payment guidance, I'm here to make the process easier.
            </p>

            <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-xl" data-testid="about-trust-points">
              {TRUST_POINTS.map((p) => (
                <li key={p} className="inline-flex items-center gap-2 text-sm text-[var(--ssc-navy)] font-bold">
                  <Check size={14} className="text-[var(--ssc-blue)] shrink-0" /> {p}
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap gap-2" data-testid="about-hero-ctas">
              <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="about-text-scott">
                <MessageSquareText size={16} /> Text Scott
              </a>
              <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-navy">Find Me a Vehicle</Link>
              <Link to="/new-vehicles" className="ssc-btn ssc-btn-secondary">Shop New Vehicles</Link>
              <Link to="/used-vehicles" className="ssc-btn ssc-btn-secondary">Shop Used Vehicles</Link>
            </div>
          </div>

          <div className="md:col-span-5 order-1 md:order-2">
            <div className="relative bg-white rounded-3xl border border-[#D9E1EA] p-3 shadow-xl">
              <img
                src={ABOUT_PHOTO}
                alt="Scott Hall from Scott Sells Chevys helping Central Ohio drivers find new and used vehicles."
                className="w-full rounded-2xl object-contain bg-[#F5F8FC] aspect-[4/5]"
                loading="eager"
                width="600" height="750"
                data-testid="about-scott-photo"
              />
              <div className="absolute -bottom-3 left-4 right-4 bg-[var(--ssc-navy)] text-white rounded-xl px-4 py-2 text-xs font-extrabold uppercase tracking-widest flex items-center justify-between">
                <span>Scott Hall</span>
                <a href={siteConfig.links.facebook} target="_blank" rel="noopener noreferrer" aria-label="Scott on Facebook" className="text-[var(--ssc-silver)] hover:text-white inline-flex items-center gap-1">
                  <Facebook size={14} /> Follow
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MAIN STORY */}
      <section className="bg-white" data-testid="about-story">
        <div className="ssc-container py-14 md:py-20 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-black text-[#081426] tracking-tight">A More Personal Way to Shop for a Vehicle</h2>
          <div className="mt-6 space-y-5 text-base md:text-lg text-[var(--ssc-ink)] leading-relaxed">
            <p>My name is Scott Hall, and I've been selling cars for over 15 years. In that time, I've helped a lot of people find vehicles that actually fit their lives, not just whatever happens to be sitting on the lot.</p>
            <p>I grew up on the Northside of Columbus, Ohio, and I know how people around Central Ohio shop. Some customers want a dependable SUV for the family. Some need a truck for work. Some are trying to keep payments comfortable. Some want to understand EVs before making the jump. Some just want someone who will answer questions without making the process feel awkward.</p>
            <p>That's why I built <strong>Scott Sells Chevys</strong>. It gives customers a simpler way to shop new and used vehicles, ask questions, value a trade, compare options, and reach me directly.</p>
            <p>After more than 15 years in the car business, I've learned that most people do not want games, pressure, or confusing answers. They want someone who will listen, explain things clearly, and help them figure out what actually makes sense.</p>
            <p className="font-bold text-[var(--ssc-navy)]">My goal is simple: make the car-buying experience easier, more honest, and a little less stressful.</p>
          </div>
        </div>
      </section>

      {/* 3. HOW I HELP */}
      <section className="bg-[#F5F8FC]" data-testid="about-help">
        <div className="ssc-container py-14 md:py-20">
          <h2 className="text-3xl md:text-4xl font-black text-[#081426] tracking-tight">How I Help</h2>
          <p className="text-[var(--ssc-ink-2)] mt-2 max-w-2xl">Pick the path that fits where you are — I'll meet you there.</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HELP_CARDS.map(({ Icon, title, text, cta, to, testid }) => (
              <div key={title} className="bg-white border border-[#D9E1EA] rounded-2xl p-5 flex flex-col" data-testid={testid}>
                <div className="w-11 h-11 rounded-xl bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] flex items-center justify-center">
                  <Icon size={22} />
                </div>
                <h3 className="text-lg font-black text-[#081426] mt-3">{title}</h3>
                <p className="text-sm text-[var(--ssc-ink-2)] mt-2 flex-1">{text}</p>
                <Link to={to} className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[var(--ssc-blue)] hover:gap-2 transition-all">
                  {cta} <ChevronRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. VALUES */}
      <section className="bg-white" data-testid="about-values">
        <div className="ssc-container py-14 md:py-20">
          <h2 className="text-3xl md:text-4xl font-black text-[#081426] tracking-tight">What You Can Expect From Me</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {VALUES.map(({ Icon, title, text }) => (
              <div key={title} className="bg-[#F5F8FC] border border-[#D9E1EA] rounded-2xl p-5">
                <Icon size={22} className="text-[var(--ssc-blue)]" />
                <h3 className="text-base font-black text-[#081426] mt-3">{title}</h3>
                <p className="text-sm text-[var(--ssc-ink-2)] mt-1">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PERSONAL TOUCH */}
      <section className="bg-[#061426]" data-testid="about-personal">
        <div className="ssc-container py-14 md:py-20 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">No Pressure. Just Help.</h2>
          <div className="mt-5 space-y-4 text-base md:text-lg text-[#b9c2d2] leading-relaxed">
            <p>Some customers are ready today. Some need time. Some need to talk through options with family. Some are just trying to figure out what makes sense. That's completely fine.</p>
            <p className="text-white font-semibold">My job is to help you get good information, understand your choices, and feel comfortable with the next step.</p>
          </div>
          <a href={siteConfig.scott.phoneSms} className="mt-6 ssc-btn ssc-btn-primary inline-flex" data-testid="about-personal-cta">
            <MessageSquareText size={16} /> Text Scott Anytime
          </a>
        </div>
      </section>

      {/* 6. CENTRAL OHIO */}
      <section className="bg-white" data-testid="about-cities">
        <div className="ssc-container py-14 md:py-20">
          <h2 className="text-3xl md:text-4xl font-black text-[#081426] tracking-tight">Proudly Helping Drivers Across Central Ohio</h2>
          <p className="text-[var(--ssc-ink-2)] mt-3 max-w-3xl">
            I proudly help drivers across these Central Ohio communities — if your town isn't listed, reach out, I likely cover it.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2" data-testid="about-cities-list">
            {CITIES.map((c) => (
              <li key={c} className="inline-flex items-center gap-1 bg-[#F5F8FC] border border-[#D9E1EA] text-[var(--ssc-navy)] font-bold text-sm px-3 py-1.5 rounded-full">
                <MapPin size={12} /> {c}
              </li>
            ))}
          </ul>
          <Link to="/service-areas" className="mt-6 ssc-btn ssc-btn-secondary inline-flex" data-testid="about-cities-cta">
            View Service Areas <ChevronRight size={14} />
          </Link>
        </div>
      </section>

      {/* 7. WHY I BUILT */}
      <section className="bg-[#F5F8FC]" data-testid="about-why">
        <div className="ssc-container py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-[#081426] tracking-tight">Why I Built Scott Sells Chevys</h2>
            <div className="mt-5 space-y-4 text-base md:text-lg text-[var(--ssc-ink)] leading-relaxed">
              <p>I built this site because car shopping should be easier.</p>
              <p>Instead of bouncing around confusing listings, forms, and dealership pages, this gives you one place to search new vehicles, search used vehicles, ask me a question, value your trade, preview a deal in Build My Own Deal, get payment help, set vehicle alerts, schedule a test drive, and contact me directly.</p>
              <p className="font-bold text-[var(--ssc-navy)]">It is meant to be simple, helpful, and personal.</p>
            </div>
          </div>
          <ul className="bg-white border border-[#D9E1EA] rounded-2xl p-6 space-y-3" data-testid="about-site-features">
            {SITE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-base text-[#081426]">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--ssc-blue)] text-white flex items-center justify-center">
                  <Check size={14} />
                </span>
                <span className="font-semibold">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ CUSTOMER PHOTO GALLERY ============ */}
      <section className="bg-[var(--ssc-silver-2)]" data-testid="about-customer-gallery">
        <div className="ssc-container py-14 md:py-20">
          <div className="text-center mb-8 md:mb-10">
            <div className="inline-block px-3 py-1 rounded-full bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] text-xs font-black uppercase tracking-wider">Real customers · real keys</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-black text-[#081426] tracking-tight">A Few Recent Deliveries</h2>
            <p className="mt-2 text-[var(--ssc-ink-2)] max-w-2xl mx-auto">Some of the families and folks I've had the privilege of helping. Could be you next.</p>
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {[
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030815/family_zxltfg.jpg",      alt: "A happy family picking up their new vehicle with Scott" },
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030816/the_crew_etvppe.jpg",    alt: "A whole crew alongside their new ride" },
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030817/The_Daley_crew_cscnw7.jpg", alt: "The Daley crew with their new vehicle" },
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030818/Lee_xmrisw.jpg",          alt: "Lee with their new vehicle" },
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030820/Jason_ovlgts.jpg",       alt: "Jason picking up his new vehicle" },
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030821/Carly_a0o1gw.jpg",       alt: "Carly with her new vehicle" },
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030822/Jennifer_qrs1ay.jpg",    alt: "Jennifer with her new vehicle" },
              { src: "https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030823/Chrissy_pwasbt.jpg",     alt: "Chrissy with her new vehicle" },
            ].map((img, i) => (
              <li key={img.src} className="group rounded-2xl overflow-hidden bg-white border border-[#D9E1EA] shadow-sm hover:shadow-xl hover:border-[var(--ssc-blue)] transition" data-testid={`gallery-photo-${i + 1}`}>
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full aspect-square object-cover transition group-hover:scale-[1.03]"
                />
              </li>
            ))}
          </ul>
          <p className="text-center mt-6 text-sm text-[var(--ssc-ink-2)]">
            Want your spot here? Pick up your next vehicle through me and I'll save you a tile.
          </p>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="bg-[#020B16]" data-testid="about-final-cta">
        <div className="ssc-container py-14 md:py-20 text-center">
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">Ready to Start?</h2>
          <p className="text-[#b9c2d2] mt-3 max-w-2xl mx-auto text-base md:text-lg">
            Tell me what you are looking for, and I'll help you narrow it down.
          </p>
          <div className="mt-7 flex flex-wrap gap-2 justify-center">
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-primary" data-testid="about-cta-finder">Find Me a Vehicle</Link>
            <Link to="/new-vehicles" className="ssc-btn ssc-btn-secondary">Shop New Vehicles</Link>
            <Link to="/used-vehicles" className="ssc-btn ssc-btn-secondary">Shop Used Vehicles</Link>
            <Link to="/value-my-trade" className="ssc-btn ssc-btn-secondary">Value My Trade</Link>
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary"><MessageSquareText size={16} /> Text Scott</a>
          </div>
          <div className="mt-8 max-w-xl mx-auto">
            {/* ReviewCta removed — gated to lead-submit success only */}
          </div>
        </div>
      </section>
    </main>
  );
}
